import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

// import { ReactComponent as Call } from '../../assets/icons/profile/call.svg';
// import { ReactComponent as Chat } from '../../assets/icons/profile/chat.svg';
import { ReactComponent as TimerIcon } from '../../assets/icons/clock-icon.svg';
import { ReactComponent as FacilityArrow } from '../../assets/icons/profile/facilityNavigate.svg';
import { ReactComponent as FileIcon } from '../../assets/icons/profile/file.svg';
import { ReactComponent as ImageEdit } from '../../assets/icons/profile/image-edit.svg';
import { ReactComponent as ProfileEdit } from '../../assets/icons/profile/profileEdit.svg';
// import { ReactComponent as CustomerCare } from '../../assets/icons/profile/support-icon.svg';
import { ReactComponent as Settings } from '../../assets/icons/profile/settings-icon.svg';
import { ReactComponent as Tick } from '../../assets/icons/tick-icon.svg';
import ProfileImage from '../../assets/images/profile.jpg';
import { useAppBranding, currentBranding } from '../../config/branding';
import { branding } from '../../config/enum';
import {
  ProfileInterface,
  UpdateEmail,
  UpdatePhoneNumber,
} from '../../interface/profile.interface';
import { useAppDispatch } from '../../redux/hooks';
import { setNotificationDetails } from '../../redux/slice/NotificationSlice';
import { fetchSessionUser } from '../../redux/slice/UserManagementSlice';
import MyButton from '../../shared-components/button/Button';
// import LanguageDropDown from '../../shared-components/custom-language-dropDown/languageDropDown';
import CustomInput from '../../shared-components/custom-input/CustomInput';
import CustomPhoneNumber from '../../shared-components/custom-phone-number-input/CustomPhoneNumberInput';
import ModalBox from '../../shared-components/modal-box/ModalBox';
import PageTitle from '../../shared-components/page-title/PageTitle';
import { getCookie } from '../../shared-functions/Cookies';
import { decodeJWT } from '../../shared-functions/JWTDocodeFunction';
import { getLocalStorageItem } from '../../shared-functions/LocalStorage';
import {
  emailValidation,
  integerValidation,
  phoneNumberValidation,
} from '../../shared-functions/Validation';
import account from '../../utils/api/account';
import profileApi from '../../utils/api/profile';
import userApi from '../../utils/api/users';

import RevenueDetails from './components/revenue-details';
import RevenueHistory from './components/revenue-history';
import WithdrawalHistory from './withdrawalHistory';

interface ModalBoxDetail {
  title: string | null;
  content: JSX.Element | null;
  notificationModal: boolean;
  header: boolean;
}

/**
 * profile main component function
 * @returns
 */
function Profile() {
  const appBranding = useAppBranding();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation('profile');
  const accountVerificationStatus = getLocalStorageItem('accountVerificationStatus');
  const fileInputReference = useRef<HTMLInputElement | null>(null);
  const InputReference = useRef<HTMLInputElement | null>(null);
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [editEmailMode, setEditEmailMode] = useState<boolean>(false);
  const [accountId, setAccountId] = useState<string | null>(null);
  const [emailForm, setEmailForm] = useState<{
    email: string;
    otp: number | null;
    buttonLoader?: boolean;
  }>({
    email: '',
    otp: null,
    buttonLoader: false,
  });
  const [editPhoneNumberMode, setEditPhoneNumberMode] = useState<boolean>(false);
  const [phoneNumberForm, setPhoneNumberForm] = useState<{
    phoneNumber: string;
    countryCode: string;
    otp: number | null;
    buttonLoader?: boolean;
  }>({
    phoneNumber: '',
    countryCode: currentBranding === 'energic' ? '966' : '1',
    otp: null,
    buttonLoader: false,
  });
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: null,
    notificationModal: false,
    header: false,
  });

  const [userProfile, setUserProfile] = useState<ProfileInterface>({});

  const intervalReference = useRef<NodeJS.Timeout | null>(null);
  const [resendTimer, setResendTimer] = useState<number>(0);

  useEffect(() => {
    const accessToken = getCookie('ev-access-token');
    let id = getCookie('accountCustomId');
    if (accessToken) {
      const payload = decodeJWT(accessToken);
      if (payload?.accountCustomId) {
        id = payload.accountCustomId;
      }
    }
    setAccountId(id);
  }, []);

  /**
   * documentEdit
   */
  const documentEdit = () => {
    if (InputReference.current) {
      InputReference.current.click();
    }
  };
  /**
   * profileEdit
   */
  const profileEdit = () => {
    if (fileInputReference.current) {
      fileInputReference.current.click();
    }
  };

  /**
   * history
   */
  const withdrawalHistoryChange = () => {
    setModalBoxDetail({
      title: t('payoutHistory'),
      content: <WithdrawalHistory />,
      notificationModal: false,
      header: true,
    });
    setModalBoxStatus('open');
  };

  /**
   * Get the profile details in function
   */
  const fetchProfileData = () => {
    Promise.allSettled([
      profileApi.getProfileDetails(),
      profileApi.getTotalChargingStation(),
      profileApi.getTotalChargingPoints(),
      profileApi.getTotalConnectors(),
    ])
      .then((response: { status: string; value?: any }[]) => {
        response.forEach((value) => {
          if (value.status === 'fulfilled') {
            setUserProfile((previous) => ({
              ...previous,
              ...value.value.data,
            }));
          }
        });
      })
      .catch((error) => {
        console.log(error);
      });
  };

  /**
   * Function to update profile image
   */
  const handleUpdateProfileImage = async (file: File) => {
    try {
      // Send the formData to your API
      await userApi.editUserProfileImage({ profileImage: file });
      fetchProfileData();
      dispatch(fetchSessionUser(userProfile._id || ''));
    } catch (error) {
      console.error('Error updating profile image:', error);
    }
  };

  /**
   * Function to update government Id
   */
  const handleUpdateGovernmentId = async (file: File) => {
    try {
      // Send the formData to your API
      await userApi.editUserGovernmentId({ governmentId: file });
      fetchProfileData();
    } catch (error) {
      console.error('Error updating profile image:', error);
    }
  };

  /**
   * Function to handle email change
   */
  const handleEmailFormChange = (name: string, value: string | number) => {
    setEmailForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  /**
   * Function to toggle email edit mode
   */
  const toggleEmailInput = () => {
    setEditEmailMode(!editEmailMode);
  };

  /**
   * Function to handle Resend OTP timer
   */
  const startResendTimer = () => {
    // Clear any previous interval before starting a new one
    if (intervalReference.current) clearInterval(intervalReference.current);

    setResendTimer(30); // Reset to 60 seconds
    intervalReference.current = setInterval(() => {
      setResendTimer((previous) => {
        if (previous <= 1) {
          if (intervalReference.current) clearInterval(intervalReference.current); // Clear interval at the end
          return 0;
        }
        return previous - 1;
      });
    }, 1000);
  };

  /**
   * Function to handle phone number change
   */
  const handlePhoneNumberFormChange = (name: string, value: string | number) => {
    setPhoneNumberForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  /**
   * Function to toggle phone number edit mode
   */
  const togglePhoneNumberInput = () => {
    setEditPhoneNumberMode(!editPhoneNumberMode);
  };

  /**
   * Function to update email
   */
  const submitUpdateEmail = (purpose: string) => {
    const body: UpdateEmail = {
      email: emailForm.email,
      purpose,
      otpType: 'updateEmail',
    };
    if (purpose === 'sendOtp') {
      if (resendTimer === 0) {
        startResendTimer();
      }
    } else if (purpose === 'verifyOtp') {
      setEmailForm((previous) => ({
        ...previous,
        buttonLoader: true,
      }));
      body.otp = emailForm.otp;
    }

    account
      .updateEmailPhoneNumber(body)
      .then(() => {
        setEmailForm((previous) => ({
          ...previous,
          buttonLoader: false,
        }));

        if (purpose === 'verifyOtp') {
          setUserProfile((previous) => ({
            ...previous,
            email: emailForm.email,
          }));
          dispatch(
            setNotificationDetails({
              type: 'success',
              notificationDetails: {
                statusCode: 200,
                message: t('emailUpdatedSuccessfully'),
              },
            }),
          );
          toggleEmailInput();
        }
      })
      .catch((error) => {
        setEmailForm((previous) => ({
          ...previous,
          buttonLoader: false,
        }));
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  /**
   * Function to update email
   */
  const submitUpdatePhoneNumber = (purpose: string) => {
    const body: UpdatePhoneNumber = {
      phoneNumber: phoneNumberForm.phoneNumber,
      countryCode: phoneNumberForm.countryCode,
      purpose,
      otpType: 'updatePhoneNumber',
    };
    if (purpose === 'sendOtp') {
      if (resendTimer === 0) {
        startResendTimer();
      }
    } else if (purpose === 'verifyOtp') {
      setPhoneNumberForm((previous) => ({
        ...previous,
        buttonLoader: true,
      }));
      body.otp = phoneNumberForm.otp;
    }

    account
      .updateEmailPhoneNumber(body)
      .then(() => {
        setPhoneNumberForm((previous) => ({
          ...previous,
          buttonLoader: false,
        }));

        if (purpose === 'verifyOtp') {
          setUserProfile((previous) => ({
            ...previous,
            countryCode: phoneNumberForm.countryCode,
            phoneNumber: phoneNumberForm.phoneNumber,
          }));
          dispatch(
            setNotificationDetails({
              type: 'success',
              notificationDetails: {
                statusCode: 200,
                message: t('phoneNumberUpdatedSuccessfully'),
              },
            }),
          );
          togglePhoneNumberInput();
        }
      })
      .catch((error) => {
        setPhoneNumberForm((previous) => ({
          ...previous,
          buttonLoader: false,
        }));
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  const [stripeId, setStripeID] = useState('');

  /**
   * Fetch Stripe settings
   */
  const fetchStripeSettings = () => {
    profileApi
      .getStripeSettings()
      .then((response) => {
        setStripeID(response.data.stripeConnectAccountId);
      })
      .catch((error) => {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  /**
   * Navigate to settings page
   */
  const handleRoute = () => {
    navigate('/ev-admin/settings');
  };

  useEffect(() => {
    fetchStripeSettings();
    fetchProfileData();
  }, []);

  return (
    <div className="profile-main-container" dir={i18n.dir()}>
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        modalBoxAnimation="modalAnimateToLeft"
        header={modalBoxDetail.header}
        notification={modalBoxDetail.notificationModal}
      />
      <div className="profile-header">
        <div className="profile-title">
          <PageTitle title={t('profile')} />
        </div>
        <div className="profile-header-buttons">
          <div className="language-dropdown">{/* <LanguageDropDown /> */}</div>
          <div className="settings-button">
            <MyButton
              label={t('settings')}
              buttonType="primary-outline"
              svgIcon={<Settings />}
              onClickFunc={handleRoute}
            />
          </div>
        </div>
      </div>
      <div className="profile-content-container">
        <div className="profile-details-container">
          <div className="profile-details">
            <div className="profile-details-edit">
              <div className="profile-image-wrap">
                <img
                  src={userProfile?.profileImage?.signedUrl || ProfileImage}
                  alt={t('preview')}
                  className="profile-image"
                />
                <div className="image-upload-icon" role="presentation" onClick={profileEdit}>
                  <ImageEdit />
                  <input
                    onChange={(event) => {
                      const file = event.target.files ? event.target.files[0] : null;
                      if (file) {
                        handleUpdateProfileImage(file);
                      }
                    }}
                    type="file"
                    accept="image/jpeg, image/png, image/gif" // Only allows jpg, png, and gif
                    ref={fileInputReference}
                    style={{ display: 'none' }}
                  />
                </div>
              </div>
              <div className="profile-content-list">
                <div className="profile-name">
                  <div className="name-active">
                    <p>{userProfile.name}</p>
                    {accountVerificationStatus === 'approved' && (
                      <span>
                        <Tick />
                      </span>
                    )}
                  </div>
                  {/* <p className="document-id">ENR0089</p> */}
                  <div className="government-id-edit">
                    <p>{t('governmentID')}:</p>
                    <div className="profile-document">
                      <span>
                        <FileIcon />
                      </span>
                      <p>{userProfile?.governmentId?.[0]?.fileName}</p>
                    </div>
                    <div className="docs-edit" role="presentation" onClick={documentEdit}>
                      <ProfileEdit />
                      <input
                        onChange={(event) => {
                          const file = event.target.files ? event.target.files[0] : null;
                          if (file) {
                            handleUpdateGovernmentId(file);
                          }
                        }}
                        ref={InputReference}
                        type="file"
                        accept="application/pdf" // Only allows PDF files                                              ref={fileInputReference}
                        style={{ display: 'none' }}
                      />
                    </div>
                  </div>
                  <div>
                    <p>
                      {t('accountID')}: {accountId}
                    </p>
                  </div>
                </div>
              </div>
              {/* <div className="profile-edit-icon">
                                <div
                                    className="icon"
                                    onClick={profileEdit}
                                    role="presentation"
                                >
                                    <ProfileEdit />
                                </div>
                            </div> */}
            </div>
            <div className="profile-facilities-details">
              <div className="profile-facilities-header">
                <div className="facility-icon">
                  <appBranding.icons.facilityIcon />
                </div>
                <p>{t('facilities')}</p>
                <div className="facility-navigate-icon">
                  <span>
                    <FacilityArrow />
                  </span>
                </div>
              </div>
              <div className="station-details">
                <div className="facility-count">
                  <p>{t('totalFacilities')}</p>
                  <span>{userProfile.totalChargingStations}</span>
                </div>
                <div className="facility-count">
                  <p>{t('totalDevices')}</p>
                  <span>{userProfile.totalChargingPoints}</span>
                </div>
                <div className="facility-count">
                  <p>{t('totalConnectors')}</p>
                  <span>{userProfile.totalConnectors}</span>
                </div>
              </div>
              <div className="contact-details">
                <div className="contact-header">
                  <div className="contact-icon">
                    <appBranding.icons.contactIcon />
                  </div>
                  <span>{t('contact')}</span>
                </div>
                <div className="contact-content">
                  {!editEmailMode ? (
                    <div className="each-contact-content">
                      <p>{t('email')}:</p>
                      <span>{userProfile.email}</span>
                      <ProfileEdit onClick={toggleEmailInput} />
                    </div>
                  ) : (
                    <>
                      <CustomInput
                        inputLabel={t('email')}
                        type="text"
                        placeholder={t('enterEmail')}
                        name="email"
                        value={emailForm.email ?? ''}
                        onChangeValue={handleEmailFormChange}
                        valueRequired
                        error={!emailValidation(emailForm.email ?? '')}
                        // errorMessage="Invalid email"
                        errorMessage={
                          emailForm.email.length === 0 ? t('emailIsRequired') : t('invalidEmail')
                        }
                      />
                      <CustomInput
                        inputLabel={t('otp')}
                        type="number"
                        placeholder={t('enterOtp')}
                        name="otp"
                        value={emailForm.otp ?? ''}
                        onChangeValue={handleEmailFormChange}
                        valueRequired
                        error={!integerValidation(1, 10, emailForm.otp ?? 0)}
                        errorMessage={t('invalidOtp')}
                      />
                      <div className="resend-otp-wrap">
                        <div className="resend-timer-wrap">
                          <span className="resend-icon">
                            <TimerIcon />
                          </span>
                          <span>00:{resendTimer < 10 ? `0${resendTimer}` : resendTimer}</span>
                        </div>
                        <p
                          role="presentation"
                          onClick={() => submitUpdateEmail('sendOtp')}
                          className={`resendOtp ${resendTimer > 0 ? 'disabled' : ''}`}>
                          {t('sendResendOTP')}
                        </p>
                      </div>
                      <div className="form-buttons-wrap">
                        <MyButton
                          label={t('cancel')}
                          buttonType="back"
                          onClickFunc={toggleEmailInput}
                        />
                        <MyButton
                          label={t('updateEmail')}
                          buttonType="submit"
                          onClickFunc={() => submitUpdateEmail('verifyOtp')}
                          disableButton={emailForm.buttonLoader}
                          loading={emailForm.buttonLoader}
                        />
                      </div>
                    </>
                  )}
                  {!editPhoneNumberMode ? (
                    <div className="each-contact-content">
                      <p>{t('phone')}:</p>
                      <span>
                        +{userProfile.countryCode} {userProfile.phoneNumber}
                      </span>
                      <ProfileEdit onClick={togglePhoneNumberInput} />
                    </div>
                  ) : (
                    <>
                      <div className="add-user-name-input">
                        <CustomPhoneNumber
                          inputLabel={t('phoneNumber')}
                          placeholder={t('enterPhoneNumber')}
                          name="phoneNumber"
                          value={phoneNumberForm.phoneNumber}
                          countryCode={phoneNumberForm.countryCode}
                          onChangeValue={handlePhoneNumberFormChange}
                          valueRequired
                          error={!phoneNumberValidation(phoneNumberForm.phoneNumber)}
                          errorMessage={t('invalidPhoneNumber')}
                        />
                      </div>
                      <CustomInput
                        inputLabel={t('otp')}
                        type="number"
                        placeholder={t('enterOtp')}
                        name="otp"
                        value={phoneNumberForm.otp ?? ''}
                        onChangeValue={handlePhoneNumberFormChange}
                        valueRequired
                        error={!integerValidation(1, 10, phoneNumberForm.otp ?? 0)}
                        errorMessage={t('invalidOtp')}
                      />
                      <div className="resend-otp-wrap">
                        <div className="resend-timer-wrap">
                          <span className="resend-icon">
                            <TimerIcon />
                          </span>
                          <span>00:{resendTimer < 10 ? `0${resendTimer}` : resendTimer}</span>
                        </div>
                        <p
                          role="presentation"
                          onClick={() => submitUpdatePhoneNumber('sendOtp')}
                          className={`resendOtp ${resendTimer > 0 ? 'disabled' : ''}`}>
                          {t('sendResendOTP')}
                        </p>
                      </div>
                      <div className="form-buttons-wrap">
                        <MyButton
                          label={t('cancel')}
                          buttonType="back"
                          onClickFunc={togglePhoneNumberInput}
                        />
                        <MyButton
                          label={t('updatePhoneNumber')}
                          buttonType="submit"
                          onClickFunc={() => submitUpdatePhoneNumber('verifyOtp')}
                          disableButton={phoneNumberForm.buttonLoader}
                          loading={phoneNumberForm.buttonLoader}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
              {currentBranding === branding.VOLTIE && (
                <div className="contact-details-stripe">
                  <div className="contact-header-stripe">
                    <div className="contact-icon">
                      <appBranding.icons.WalletIcon />
                    </div>
                    <span>{t('stripeConnectID')}</span>
                  </div>

                  <div>
                    <p>
                      {stripeId ? (
                        <p className="stripe-Id-show">{stripeId}</p>
                      ) : (
                        <>
                          {t('thisFieldHasBeenRemovedYouCanNowFindItUnder')}{' '}
                          <Link to="/ev-admin/settings" className="setting-text">
                            {t('settings→')}
                          </Link>{' '}
                          {t('payoutsForEasierAccessAndManagement')}
                        </>
                      )}
                    </p>{' '}
                  </div>
                </div>
              )}
              {/* <div className="customer-care">
                <div className="customer-care-header">
                  <span>
                    <CustomerCare />
                  </span>
                  <p>Customer Care</p>
                </div>
                <div className="contact-call">
                  <MyButton
                    label="Call"
                    disableHoverEffect
                    buttonType="back"
                    onClickFunc={() => {}}
                    svgIcon={<Call />}
                  />
                </div>
                <div className="contact-chat">
                  <MyButton
                    label="Chat"
                    disableHoverEffect
                    buttonType="back"
                    onClickFunc={() => {}}
                    svgIcon={<Chat />}
                  />
                </div>
              </div> */}
            </div>
          </div>
          <RevenueDetails withdrawalHistoryChange={withdrawalHistoryChange} />
        </div>
      </div>
      <RevenueHistory />
    </div>
  );
}

export default Profile;
